import {
  capitalizeFirstLetter,
  CapitalMovementTypeEnum,
  CustomPalette,
  DepartmentEnum,
  RoutePaths,
  TaskManagementEventTypeEnum,
  TaskPriorityEnum,
  TaskStatusEnum,
  UserInteractionTypeEnum,
} from "config";

import {
  StyledConstantsDragHandleIcon,
  StyledConstantsDragHandleIconAlternate,
  StyledConstantsKeyboardDoubleArrowDownIcon,
  StyledConstantsKeyboardDoubleArrowDownIconAlternate,
  StyledConstantsKeyboardDoubleArrowUpIcon,
  StyledConstantsKeyboardDoubleArrowUpIconAlternate,
} from "./styles";

export const initialQuillValue = "<p><br></p>";
export const emptyQuillValue = `<p style="margin: 0px;"><br></p>`;
export const MAX_32_BIT_INT = 2147483647;
export const NOT_AVAILABLE_INFO = "N/A";
export const minDate = -86400000000000;
export const maxDate = 86400000000000;
export const RADIAN = Math.PI / 180;

export const customCapitalMovementsTypeNames = {
  [CapitalMovementTypeEnum.ADDON_SUBSCRIPTION]: "Add-on investment",
  [CapitalMovementTypeEnum.DIVIDEND_INVESTMENT_SUBSCRIPTION]:
    "Dividend re-investment",
} as Record<CapitalMovementTypeEnum, string>;

export const amountFilterRangeData = [
  [0, 25000],
  [25000, 50000],
  [50000, 75000],
  [75000, 100000],
  [100000],
];

export const FULL_DATE_FORMAT = "M/d/yyyy";
export const TIME_FORMAT = "h:m:ss aa";
export const TIME_WITHOUT_SECONDS_FORMAT = "h:mm aa";
export const INVESTOR_CATEGORY_CONSTANT = "Investor";
export const LINK_AGENT_CATEGORY_CONSTANT = "Capital Introducer";
export const EMPLOYEE_CATEGORY_CONSTANT = "Employee";
export const SELECT_ALL_SELECT_OPTION = "selectAll";
export const VARVCF_FUND_NAME = "varvcf";
export const VARDFI_FUND_NAME = "vardfi";
export const ADMINISTRATION_DEPARTMENT_CONSTANT = 6;

export const LinkAgentUserRoutes = [
  RoutePaths.USER_CAPITAL_INTRODUCER_MY_INFO,
  RoutePaths.USER_CAPITAL_INTRODUCER_PAYOUTS,
  RoutePaths.USER_CAPITAL_INTRODUCER_REPORTS,
  RoutePaths.USER_CAPITAL_INTRODUCER_RESOURCES,
];

export const InvestorUserRoutes = [
  RoutePaths.USER_INVESTOR_DASHBOARD,
  RoutePaths.USER_INVESTOR_CAPITAL_MOVEMENT,
  RoutePaths.USER_INVESTOR_MY_INFO,
  RoutePaths.USER_INVESTOR_REPORTS,
  RoutePaths.USER_INVESTOR_STATEMENTS,
];

export const userInteractionTypeToSelectiveOptions = Object.values(
  UserInteractionTypeEnum
).map((el, idx) => ({ value: `${idx + 1}`, label: capitalizeFirstLetter(el) }));

export const taskStatusToColorMap: Record<TaskStatusEnum, CustomPalette> = {
  [TaskStatusEnum.BLOCKED]: CustomPalette.red,
  [TaskStatusEnum.COMPLETED]: CustomPalette.light_green,
  [TaskStatusEnum.DECLINED]: CustomPalette.black,
  [TaskStatusEnum.IN_PROGRESS]: CustomPalette.golden,
  [TaskStatusEnum.IN_REVIEW]: CustomPalette.lighter_blue,
  [TaskStatusEnum.NOT_STARTED]: CustomPalette.darker_gray,
};

export const taskStatusToTextMap: Record<TaskStatusEnum, string> = {
  [TaskStatusEnum.BLOCKED]: "Blocked",
  [TaskStatusEnum.COMPLETED]: "Completed",
  [TaskStatusEnum.DECLINED]: "Declined",
  [TaskStatusEnum.IN_PROGRESS]: "In Progress",
  [TaskStatusEnum.IN_REVIEW]: "Review Needed",
  [TaskStatusEnum.NOT_STARTED]: "Not Started",
};

export const taskPriorityToColorMap: Record<number, any> = {
  [TaskPriorityEnum.LOW]: CustomPalette.lighter_blue,
  [TaskPriorityEnum.MED]: CustomPalette.golden,
  [TaskPriorityEnum.HIGH]: CustomPalette.red,
};

export const taskPriorityToIconMap: Record<number, any> = {
  [TaskPriorityEnum.LOW]: (
    <StyledConstantsKeyboardDoubleArrowDownIcon
      taskPriorityToColorMapTaskPriorityEnumLOW={
        taskPriorityToColorMap[TaskPriorityEnum.LOW]
      }
    />
  ),
  [TaskPriorityEnum.MED]: (
    <StyledConstantsDragHandleIcon
      taskPriorityToColorMapTaskPriorityEnumMED={
        taskPriorityToColorMap[TaskPriorityEnum.MED]
      }
    />
  ),
  [TaskPriorityEnum.HIGH]: (
    <StyledConstantsKeyboardDoubleArrowUpIcon
      taskPriorityToColorMapTaskPriorityEnumHIGH={
        taskPriorityToColorMap[TaskPriorityEnum.HIGH]
      }
    />
  ),
};

export const taskPriorityToSmallIconMap: Record<number, any> = {
  [TaskPriorityEnum.LOW]: (
    <StyledConstantsKeyboardDoubleArrowDownIconAlternate
      taskPriorityToColorMapTaskPriorityEnumLOW={
        taskPriorityToColorMap[TaskPriorityEnum.LOW]
      }
    />
  ),
  [TaskPriorityEnum.MED]: (
    <StyledConstantsDragHandleIconAlternate
      taskPriorityToColorMapTaskPriorityEnumMED={
        taskPriorityToColorMap[TaskPriorityEnum.MED]
      }
    />
  ),
  [TaskPriorityEnum.HIGH]: (
    <StyledConstantsKeyboardDoubleArrowUpIconAlternate
      taskPriorityToColorMapTaskPriorityEnumHIGH={
        taskPriorityToColorMap[TaskPriorityEnum.HIGH]
      }
    />
  ),
};

export const taskDepartmentToColorMap: Record<string, CustomPalette> = {
  [DepartmentEnum.VENTURE]: CustomPalette.yellow,
  [DepartmentEnum.QUANTITATIVE]: CustomPalette.purple,
  [DepartmentEnum.CAPITAL_FORMATION]: CustomPalette.red,
  [DepartmentEnum.FINANCE]: CustomPalette.default_green,
  [DepartmentEnum.ADMINISTRATION]: CustomPalette.orange,
  [DepartmentEnum.MANAGEMENT]: CustomPalette.black,
};

export const defaultTaskDepartmentColor = CustomPalette.darker_gray;

export const taskManagementEventTypeToColorMap: Record<number, CustomPalette> =
  {
    [TaskManagementEventTypeEnum.TASK]: CustomPalette.dark_blue,
    [TaskManagementEventTypeEnum.REMIND]: CustomPalette.golden,
    [TaskManagementEventTypeEnum.HOLIDAY]: CustomPalette.light_green,
    [TaskManagementEventTypeEnum.LEAVE]: CustomPalette.red,
    [TaskManagementEventTypeEnum.OTHER]: CustomPalette.middle_blue,
  };

export const defaultTaskManagementEventTypeColor = CustomPalette.darker_gray;

export const ChartPalette = [
  "#0681B6",
  "#2AA1D4",
  "#50BBE9",
  "#71C6EB",
  "#90D5F2",
  "#BAE5F8",
  "#CAEFFF",
  "#DCF4FF",
  "#EEEEEE",
];

export const TimezoneList = [
  { name: "UTC", code: "" },
  { name: "America/New_York", code: "EST" }, // Eastern Time
  { name: "America/Los_Angeles", code: "PST" }, // Pacific Time
  { name: "America/Chicago", code: "CST" }, // Central Time
  { name: "America/Denver", code: "MST" }, // Mountain Time
  { name: "Europe/London", code: "GMT" }, // Greenwich Mean Time
  { name: "Europe/Berlin", code: "CET" }, // Central European Time
  { name: "Europe/Zurich", code: "CET" }, // Central European Time
  { name: "Asia/Dubai", code: "GST" }, // Gulf Standard Time
  { name: "Asia/Bangkok", code: "ICT" }, // Indochina Time
  { name: "Asia/Tokyo", code: "JST" }, // Japan Standard Time
  { name: "Australia/Sydney", code: "AEST" }, // Australian Eastern Standard Time
];
